import { Button, cn } from '@heroui/react'
import { useBillingStatusStore } from '@qse/stores'
import { AlertTriangle, X } from 'lucide-react'
import { useEffect, useState } from 'react'

interface QuotaAdjustmentBannerProps {
  className?: string
  isInView?: boolean
  setIsQuotaAdjustmentVisible?: (show: boolean) => void
}

export const QuotaAdjustmentBanner: React.FC<QuotaAdjustmentBannerProps> = ({
  className,
  setIsQuotaAdjustmentVisible,
}) => {
  const {
    showQuotaAdjustmentBanner,
    setShowQuotaAdjustmentBanner,
    quotaAdjustmentGracePeriodStatus,
  } = useBillingStatusStore()

  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Add a small delay for smooth animation
    const timer = setTimeout(() => {
      setIsVisible(showQuotaAdjustmentBanner)
      setIsQuotaAdjustmentVisible?.(showQuotaAdjustmentBanner)
    }, 0)

    return () => clearTimeout(timer)
  }, [showQuotaAdjustmentBanner, setIsQuotaAdjustmentVisible])

  if (!isVisible || !quotaAdjustmentGracePeriodStatus?.targetPlan) {
    return null
  }

  // Convert storage quota from bytes to GB
  const targetPlanQuotaGb =
    quotaAdjustmentGracePeriodStatus.targetPlan.storageQuotaBytes / (1024 * 1024 * 1024)

  return (
    <div
      className={cn(
        'pointer-events-auto fixed inset-x-4 bottom-6 z-[110] mx-auto rounded-2xl border border-amber-400/70 bg-amber-50/95 p-5 shadow-2xl backdrop-blur-md transition-all duration-300 ease-out dark:border-amber-700/70 dark:bg-amber-900/80 md:inset-x-auto md:mx-[30px]',
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
        className,
      )}
    >
      <div className="flex flex-row items-start gap-4">
        <div className="flex-shrink-0 pt-0.5">
          <AlertTriangle
            aria-hidden="true"
            className="h-6 w-6 text-amber-700 dark:text-amber-300"
          />
        </div>
        <p className="text-sm leading-relaxed text-amber-900 dark:text-amber-50">
          Plan Downgrade Notice - Your new plan quota{' '}
          <strong>{targetPlanQuotaGb.toFixed(2)} GB</strong> is lower than your current user
          allocations. User quotas will be automatically adjusted at your next billing cycle to fit
          within your plan limits. You can manually adjust user quotas before then to avoid
          automatic reduction. If you've already adjusted quotas manually, no action is needed.
        </p>
        <Button
          isIconOnly
          aria-label="Dismiss quota adjustment notice"
          className="absolute -right-3 -top-3 h-8 w-8 min-h-8 min-w-8 bg-amber-400/20 text-amber-800 shadow-sm backdrop-blur hover:bg-amber-400/30 hover:text-amber-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500 dark:bg-amber-500/20 dark:text-amber-100 dark:hover:bg-amber-500/30 dark:hover:text-amber-50"
          color="warning"
          radius="full"
          size="sm"
          variant="flat"
          onPress={() => setShowQuotaAdjustmentBanner(false)}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
